import React, { useState, useEffect, useRef, useCallback, memo, useId } from 'react';
import { createPortal } from 'react-dom';
import styles from './WhatIfSlidersSection.module.css';
import forecastStyles from './ForecastPage.module.css';
import loadingStyles from '../../shared/Loading.module.css';
import { formatCurrency } from '../../../utils/onboardingUtils';
import { TargetArrowIcon } from '../your-quick-richuels/icons';

const WHAT_IF_WAGE_RANGE = { min: 0, max: 20000, step: 100 };
const WHAT_IF_EXPENSES_RANGE = { min: 0, max: 20000, step: 100 };
const WHAT_IF_RETIREMENT_RANGE = { min: 50, max: 70, step: 1 };

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const rangePct = (v, lo, hi) => (hi > lo ? ((clamp(v, lo, hi) - lo) / (hi - lo)) * 100 : 0);

function BarsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3.2 19.5h17.6" fill="none" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="4" y="12.5" width="4" height="6.4" rx="1.15" fill="#34d399" />
      <rect x="10" y="8.2" width="4" height="10.7" rx="1.15" fill="#38bdf8" />
      <rect x="16" y="4.2" width="4" height="14.7" rx="1.15" fill="#0369a1" />
    </svg>
  );
}

/** Local slider value + raf-batched updates; resets only when `syncedFromProp` changes. */
function useRafSyncedValue(syncedFromProp) {
  const rafIdRef = useRef(null);
  const latestValueRef = useRef(syncedFromProp);
  const [value, setValue] = useState(syncedFromProp);

  useEffect(() => {
    latestValueRef.current = syncedFromProp;
    setValue(syncedFromProp);
  }, [syncedFromProp]);

  const setValueRaf = useCallback((nextValue) => {
    latestValueRef.current = nextValue;
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      setValue(latestValueRef.current);
    });
  }, []);

  const setValueNow = useCallback((nextValue) => {
    latestValueRef.current = nextValue;
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    setValue(nextValue);
  }, []);

  useEffect(() => () => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
  }, []);

  return [value, setValueRaf, setValueNow];
}

function WhatIfSliderCard({
  label,
  hint,
  displayValue,
  pulseKey,
  isDragging,
  min,
  max,
  step,
  value,
  onChange,
  onDragStart,
  onDragEnd,
  formatTick = String,
}) {
  return (
    <div className={styles.sliderCard}>
      <div className={styles.sliderTop}>
        <div className={styles.sliderCopy}>
          <span className={styles.sliderLabel}>{label}</span>
          <span className={styles.sliderHint}>{hint}</span>
        </div>
        <span
          key={pulseKey}
          className={`${styles.sliderValue} ${!isDragging ? styles.valuePulse : ''}`}
        >
          {displayValue}
        </span>
      </div>
      <input
        type="range"
        className={styles.range}
        min={min}
        max={max}
        step={step}
        value={clamp(value, min, max)}
        onPointerDown={onDragStart}
        onPointerUp={onDragEnd}
        onTouchStart={onDragStart}
        onTouchEnd={onDragEnd}
        onMouseDown={onDragStart}
        onMouseUp={onDragEnd}
        onInput={(e) => onChange(Number(e.target.value) || 0)}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        style={{ '--fill-pct': `${rangePct(value, min, max)}%` }}
        aria-label={label}
      />
      <div className={styles.ticks}>
        <span className={styles.tick}>{formatTick(min)}</span>
        <span className={styles.tick}>{formatTick(max)}</span>
      </div>
    </div>
  );
}

const WhatIfSlidersSection = memo(function WhatIfSlidersSection({
  userMonthlyIncome = 0,
  partnerMonthlyIncome = 0,
  currentMonthlyExpenses,
  currentRetirementAge,
  isRunningScenario = false,
  onRunScenario = null,
  showRunScenarioButton = true,
  scenarioStatus = 'idle',
  scenarioMessage = '',
  hasIncomeSourceChanges = false,
  children,
}) {
  const [userIncomeDraft, setUserIncomeDraftRaf, setUserIncomeDraftNow] = useRafSyncedValue(userMonthlyIncome);
  const [spouseIncomeDraft, setSpouseIncomeDraftRaf, setSpouseIncomeDraftNow] = useRafSyncedValue(partnerMonthlyIncome);
  const [monthlyExpensesDraft, setMonthlyExpensesDraftRaf, setMonthlyExpensesDraftNow] = useRafSyncedValue(currentMonthlyExpenses);
  const [retirementAgeDraft, setRetirementAgeDraftRaf, setRetirementAgeDraftNow] = useRafSyncedValue(currentRetirementAge);
  const [isDragging, setIsDragging] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);
  const scenarioResultTitleId = useId();

  const hasSliderChanges =
    (Number(userIncomeDraft) || 0) !== (Number(userMonthlyIncome) || 0) ||
    (Number(spouseIncomeDraft) || 0) !== (Number(partnerMonthlyIncome) || 0) ||
    (Number(monthlyExpensesDraft) || 0) !== (Number(currentMonthlyExpenses) || 0) ||
    (Number(retirementAgeDraft) || 0) !== (Number(currentRetirementAge) || 0);
  // Income-source edits enable Run Scenario on their own. Reset still follows the sliders.
  const hasScenarioChanges = hasSliderChanges || hasIncomeSourceChanges;

  const handleDragStart = useCallback(() => setIsDragging(true), []);
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setPulseKey((k) => k + 1);
  }, []);

  const handleReset = useCallback(() => {
    setUserIncomeDraftNow(Number(userMonthlyIncome) || 0);
    setSpouseIncomeDraftNow(Number(partnerMonthlyIncome) || 0);
    setMonthlyExpensesDraftNow(Number(currentMonthlyExpenses) || 0);
    setRetirementAgeDraftNow(Number(currentRetirementAge) || WHAT_IF_RETIREMENT_RANGE.min);
    setPulseKey((k) => k + 1);
  }, [
    userMonthlyIncome,
    partnerMonthlyIncome,
    currentMonthlyExpenses,
    currentRetirementAge,
    setUserIncomeDraftNow,
    setSpouseIncomeDraftNow,
    setMonthlyExpensesDraftNow,
    setRetirementAgeDraftNow,
  ]);

  const handleRunClick = useCallback(() => {
    if (typeof onRunScenario !== 'function') return;
    onRunScenario({
      monthly_net_income_during_career_shift: Math.max(0, Number(userIncomeDraft) || 0),
      monthly_net_income_partner: Math.max(0, Number(spouseIncomeDraft) || 0),
      monthly_expenses: monthlyExpensesDraft,
      retirement_age: retirementAgeDraft
    });
  }, [onRunScenario, userIncomeDraft, spouseIncomeDraft, monthlyExpensesDraft, retirementAgeDraft]);

  const renderRunScenarioButton = (fullWidth = false) => (
    <button
      type="button"
      className={fullWidth ? `${styles.runButton} ${styles.runButtonFullWidth}` : styles.runButton}
      onClick={handleRunClick}
      disabled={isRunningScenario || !hasScenarioChanges}
      aria-busy={isRunningScenario}
    >
      {isRunningScenario ? 'Running…' : 'Run Scenario'}
    </button>
  );

  // Slot for callers that place the button elsewhere (e.g. below extra sections).
  const runScenarioButton = showRunScenarioButton ? (
    <div className={styles.runRowFullWidth}>{renderRunScenarioButton(true)}</div>
  ) : null;

  return (
    <div className={styles.section} aria-label="What-if scenario sliders">
      <div className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>What-If Scenarios</p>
          <div className={styles.heading}>
            <h2 className={styles.title}>Runway Maximizer</h2>
            <p className={styles.subtitle}>
              Model income month by month to understand your runway and find opportunities to extend it.
            </p>
          </div>
        </div>
        <aside className={styles.banner} aria-label="Maximize runway income">
          <div className={styles.bannerIcon}>
            <TargetArrowIcon className={styles.bannerIconGlyph} />
          </div>
          <div className={styles.bannerCopy}>
            <p className={styles.bannerKicker}>Maximize runway income</p>
            <p className={styles.bannerText}>
              Explore every income option to protect your savings and build more time.
            </p>
          </div>
        </aside>
      </div>

      {isRunningScenario ? (
        <div className={styles.overlay} aria-hidden="false">
          <div className={styles.overlayInner}>
            <div className={loadingStyles['loading-spinner']} aria-hidden="true">
              <div className={loadingStyles['spinner-ring']} />
              <div className={loadingStyles['spinner-ring']} />
              <div className={loadingStyles['spinner-ring']} />
            </div>
            <div className={styles.overlayText}>
              Running your scenario…
              <div className={styles.overlayHint}>
                This usually takes a few seconds.
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <section className={styles.panel} aria-labelledby="baseline-assumptions-title">
        <div className={styles.panelHead}>
          <div className={styles.barsIcon} aria-hidden="true">
            <BarsIcon />
          </div>
          <div className={styles.panelCopy}>
            <div className={styles.panelTitleRow}>
              <h3 id="baseline-assumptions-title" className={styles.panelTitle}>
                Baseline &amp; Core Assumptions
              </h3>
              <span className={styles.leverBadge}>Foundational levers</span>
            </div>
            <p className={styles.panelSubtitle}>
              Verify existing baseline income, monthly living expenses, and horizon targets to anchor your runway model.
            </p>
          </div>
          <div className={styles.panelActions}>
            <span className={styles.baselineStatus}>
              <span className={styles.baselineDot} aria-hidden="true" />
              Baseline Active
            </span>
            <button
              type="button"
              className={styles.resetButton}
              onClick={handleReset}
              disabled={!hasSliderChanges || isRunningScenario}
            >
              Reset
            </button>
          </div>
        </div>

        <div className={styles.grid}>
          <WhatIfSliderCard
            label="User Wage Income"
            hint="Your current take-home pay"
            displayValue={formatCurrency(userIncomeDraft)}
            pulseKey={`user-${pulseKey}`}
            isDragging={isDragging}
            min={WHAT_IF_WAGE_RANGE.min}
            max={WHAT_IF_WAGE_RANGE.max}
            step={WHAT_IF_WAGE_RANGE.step}
            value={userIncomeDraft}
            onChange={setUserIncomeDraftRaf}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            formatTick={formatCurrency}
          />
          <WhatIfSliderCard
            label="Spouse Wage Income"
            hint="Spouse's current take-home pay"
            displayValue={formatCurrency(spouseIncomeDraft)}
            pulseKey={`spouse-${pulseKey}`}
            isDragging={isDragging}
            min={WHAT_IF_WAGE_RANGE.min}
            max={WHAT_IF_WAGE_RANGE.max}
            step={WHAT_IF_WAGE_RANGE.step}
            value={spouseIncomeDraft}
            onChange={setSpouseIncomeDraftRaf}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            formatTick={formatCurrency}
          />
          <WhatIfSliderCard
            label="Monthly Expenses"
            hint="Household monthly living expenses"
            displayValue={formatCurrency(monthlyExpensesDraft)}
            pulseKey={`expenses-${pulseKey}`}
            isDragging={isDragging}
            min={WHAT_IF_EXPENSES_RANGE.min}
            max={WHAT_IF_EXPENSES_RANGE.max}
            step={WHAT_IF_EXPENSES_RANGE.step}
            value={monthlyExpensesDraft}
            onChange={setMonthlyExpensesDraftRaf}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            formatTick={formatCurrency}
          />
          <WhatIfSliderCard
            label="Retirement Age"
            hint="Your target age"
            displayValue={retirementAgeDraft}
            pulseKey={`ret-${pulseKey}`}
            isDragging={isDragging}
            min={WHAT_IF_RETIREMENT_RANGE.min}
            max={WHAT_IF_RETIREMENT_RANGE.max}
            step={WHAT_IF_RETIREMENT_RANGE.step}
            value={retirementAgeDraft}
            onChange={(next) => setRetirementAgeDraftRaf(next || WHAT_IF_RETIREMENT_RANGE.min)}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        </div>
      </section>

      {typeof children === 'function' ? children({ runScenarioButton }) : children}

      {typeof children !== 'function' && !children && showRunScenarioButton ? (
        <div className={styles.runRow}>{renderRunScenarioButton()}</div>
      ) : null}

      {typeof document !== 'undefined' &&
        (scenarioStatus === 'success' || scenarioStatus === 'error') &&
        createPortal(
          <div
            className={`${forecastStyles.modalOverlay} ${forecastStyles.scenarioResultOverlay}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={scenarioResultTitleId}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              className={`${forecastStyles.scenarioResultCard} ${scenarioStatus === 'error' ? forecastStyles.scenarioResultCardError : ''}`}
            >
              <div role="status" aria-live="polite" className={forecastStyles.scenarioResultStatus}>
                <h2
                  id={scenarioResultTitleId}
                  className={`${forecastStyles.scenarioResultTitle} ${scenarioStatus === 'error' ? forecastStyles.scenarioResultTitleError : ''}`}
                >
                  {scenarioStatus === 'error' ? 'Scenario update failed' : 'Scenario applied'}
                </h2>
                <p
                  className={`${forecastStyles.scenarioResultMessage} ${scenarioStatus === 'error' ? forecastStyles.scenarioResultMessageError : ''}`}
                >
                  {scenarioMessage ||
                    (scenarioStatus === 'error'
                      ? 'We couldn’t apply your changes. Please try again.'
                      : 'Your forecast has been refreshed with this scenario.')}
                </p>
              </div>
              <button
                type="button"
                className={`${styles.runButton} ${forecastStyles.scenarioResultOk}`}
                onClick={() => {
                  window.location.reload();
                }}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
});

export default WhatIfSlidersSection;
